import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import {
  Calendar,
  dateFnsLocalizer,
  Views,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  addMonths,
  subMonths,
  Locale
} from "date-fns";
import { enUS, es } from "date-fns/locale";
import intl from "components/i18n/ReactIntlWrapper";
import { LOCALES_ISO_639_1 } from "components/i18n/locales";
import useLocale from "i18n/useLocale";
import { hasPermission } from "components/authorization/PermissionUtils";
import { SCOPES } from "common/permissions";
import { Box, Button, Typography, Stack, CircularProgress } from "@mui/material";
import { getTextColor } from "theme/styles/utils";
import ModalCalendarEvent from "./Modal/ModalCalendarEvent";

const LOCALE_MESSAGES: Partial<Record<LOCALES_ISO_639_1, Locale>> = {
  en: enUS,
  es: es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: LOCALE_MESSAGES,
});

const GenericCalendar = ({
  events,
  nonWorkingDay = false,
  isCalendarDashboard = false,
  updateEvents,
  onMonthChange,
  isLoading = false,
}) => {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventDetailOpen, setIsEventDetailOpen] = useState(false);
  const [back, setBack] = useState(true);
  const [next, setNext] = useState(true);
  const [hasEventInPreviousMonth, setHasEventInPreviousMonth] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (events) {
      checkEvents(events, setHasEventInPreviousMonth);
    }
  }, [events]);

    const closeEventDetail = () => {
    setIsEventDetailOpen(false);
    setSelectedEvent(null);
  };

  const handleSelectEvent = (event) => {
    navigate(`/Calendar/Holidays/edit/${event.id}`, { state: { from: location.pathname } });
  };

  const checkEvents = (events, setHasEventInCurrentMonth) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const result = events.some((event) => {
      const fromDate = new Date(event.start);
      const toDate = new Date(event.end);

      return (
        fromDate.getMonth() < currentMonth &&
        fromDate.getFullYear() === currentYear &&
        toDate.getMonth() === currentMonth &&
        toDate.getFullYear() === currentYear
      );
    });
    setHasEventInCurrentMonth(result);
  };

  const eventPropGetter = (event) => {
    return {
      style: {
        //color: getTextColor(event.workflowStateColor ? event.workflowStateColor : "primary"),
        backgroundColor: event.workflowStateColor ? event.workflowStateColor : "primary",
      },
    };
  };

  const handleNavigate = (action: string, currentCalendarDate: Date) => {
    let newDate = currentCalendarDate;
    
    if (action === 'PREV') {
      newDate = subMonths(currentCalendarDate, 1);
    } else if (action === 'NEXT') {
      newDate = addMonths(currentCalendarDate, 1);
    } else if (action === 'TODAY') {
      newDate = new Date();
    }
    
    setCurrentDate(newDate);
    
    // Notify parent component about month change
    if (onMonthChange && (action === 'PREV' || action === 'NEXT' || action === 'TODAY')) {
      const startOfMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
      const endOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0, 23, 59, 59);
      onMonthChange(startOfMonth, endOfMonth);
    }
  };

  const CustomToolbar = ({ label, onNavigate, date }) => {
    useEffect(() => {
      if (hasEventInPreviousMonth) {
        setBack(true);
        setNext(true);
        const current = new Date();
        const threeMonthsLater = addMonths(current, 3);
        const oneMonthBefore = subMonths(new Date(), 1);

        if (
          date.getMonth() === oneMonthBefore.getMonth() &&
          date.getFullYear() === oneMonthBefore.getFullYear()
        ) {
          setBack(false);
        }

        if (
          date.getFullYear() > threeMonthsLater.getFullYear() ||
          (date.getFullYear() === threeMonthsLater.getFullYear() &&
            date.getMonth() >= threeMonthsLater.getMonth())
        ) {
          setNext(false);
        }
      } else {
        setBack(false);
        setNext(true);
        const current = new Date();
        const threeMonthsLater = addMonths(current, 3);

        if (
          date.getFullYear() > threeMonthsLater.getFullYear() ||
          (date.getFullYear() === threeMonthsLater.getFullYear() &&
            date.getMonth() >= threeMonthsLater.getMonth())
        ) {
          setNext(false);
        }
        if (date.getMonth() > current.getMonth()) setBack(true);
      }
    }, [date, hasEventInPreviousMonth]);

    const handleToolbarNavigate = (action: string) => {
      handleNavigate(action, date);
      onNavigate(action);
    };

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={() => handleToolbarNavigate("TODAY")} disabled={isLoading}>
            {intl.get("genericCalendar.customToolbar.button.today")}
          </Button>
          {isCalendarDashboard ? (
            <>
              {back && (
                <Button variant="outlined" size="small" onClick={() => handleToolbarNavigate("PREV")} disabled={isLoading}>
                  {intl.get("genericCalendar.customToolbar.button.back")}
                </Button>
              )}
              {next && (
                <Button variant="outlined" size="small" onClick={() => handleToolbarNavigate("NEXT")} disabled={isLoading}>
                  {intl.get("genericCalendar.customToolbar.button.next")}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" onClick={() => handleToolbarNavigate("PREV")} disabled={isLoading}>
                {intl.get("genericCalendar.customToolbar.button.back")}
              </Button>
              <Button variant="outlined" size="small" onClick={() => handleToolbarNavigate("NEXT")} disabled={isLoading}>
                {intl.get("genericCalendar.customToolbar.button.next")}
              </Button>
            </>
          )}
        </Stack>
        <Typography variant="subtitle1" color="primary">{label}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ 
        position: 'relative',
        '& .rbc-event': {
          fontSize: '0.60rem', // 14px - adjust as needed
          fontWeight: 500,
        },
        '& .rbc-event-label': {
          fontSize: '0.60rem', // 12px - for event time labels
        },
        '& .rbc-event-content': {
          fontSize: '0.60rem', // 14px - for event content
        }
      }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Calendar
          localizer={localizer}
          culture={locale}
          messages={LOCALE_MESSAGES[locale]}
          events={events || []}
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          style={isCalendarDashboard ? { height: "35rem" } : { height: "40rem" }}
          views={[Views.MONTH]}
          selectable={false}
          components={{
            toolbar: CustomToolbar,
          }}
          popup
          eventPropGetter={eventPropGetter}
          onSelectEvent={
            hasPermission({ scopes: [SCOPES["nonworkingday.edit"]] }) && nonWorkingDay
              ? handleSelectEvent
              : null
          }
        />
      </Box>
      {nonWorkingDay && (
        <ModalCalendarEvent
          isOpen={isEventDetailOpen}
          closeModal={closeEventDetail}
          eventId={selectedEvent?.id}
          updateEvents={updateEvents}
        />
      )}
    </>
  );
};

export default GenericCalendar;
